import { createAsyncThunk, createEntityAdapter, createSlice, createSelector } from '@reduxjs/toolkit';
import { startLoading, stopLoading } from 'app/store/utilSlice';
import { apiGet, apiPut, apiPost, apiDel } from 'app/shared-components/util/restAPI';
import { createSuperbill } from './superbillsSlice';

const billingRecordAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.serviceDate.localeCompare(a.serviceDate)
});

export const fetchBillingRecordsByOwner = createAsyncThunk(
  'billingRecords/fetchByOwner',
  async ({ ownerKey, from, to }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/billingrecord/byowner/${ownerKey}`;
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: from && to ? { rangeCondition: 'BETWEEN', rangeValue1: from, rangeValue2: to } : {}
      };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchBillingRecordsByClient = createAsyncThunk(
  'billingRecords/fetchByClient',
  async ({ clientId, from, to }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/billingrecord/byclient/${clientId}`;
      const options = { 
        headers: {}, 
        response: false,
        queryStringParameters: from && to ? { rangeCondition: 'BETWEEN', rangeValue1: from, rangeValue2: to } : {}
      };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const createBillingRecord = createAsyncThunk(
  'billingRecords/create',
  async ({ ownerKey, recordData }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/billingrecord/';
      const options = { headers: {}, body: recordData, response: false };
      return await apiPut(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const createBillingRecordFromSession = createAsyncThunk(
  'billingRecords/createFromSession',
  async ({ sessionId, overrides }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/billingrecord/fromsession';
      const body = { sessionId, overrides };
      const options = { headers: {}, body, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateBillingRecord = createAsyncThunk(
  'billingRecords/update',
  async ({ recordId, updates }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/billingrecord/${recordId}`;
      const body = { id: recordId, ...updates };
      const options = { headers: {}, body, response: false };
      return await apiPut(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const deleteBillingRecord = createAsyncThunk(
  'billingRecords/delete',
  async (recordId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/billingrecord/${recordId}`;
      const options = { headers: {}, response: false };
      await apiDel(apiName, path, options);
      return recordId;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const getBillingRecordVersionHistory = createAsyncThunk(
  'billingRecords/getVersionHistory',
  async (recordId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/billingrecord/${recordId}/versions`;
      const options = { headers: {}, response: false };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const processBillingRecordPayment = createAsyncThunk(
  'billingRecords/processPayment',
  async (recordId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/billingrecord/${recordId}/processpayment`;
      const options = { headers: {}, response: false };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

const billingRecordsSlice = createSlice({
  name: 'billingRecords',
  initialState: billingRecordAdapter.getInitialState({
    byOwnerKey: {},
    byClient: {}
  }),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchBillingRecordsByOwner.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg.ownerKey;
        state.byOwnerKey[ownerKey] = action.payload.map(r => r.id);
        billingRecordAdapter.setMany(state, action.payload);
      })
      .addCase(fetchBillingRecordsByClient.fulfilled, (state, action) => {
        const clientId = action.meta.arg.clientId;
        state.byClient[clientId] = action.payload.map(r => r.id);
        billingRecordAdapter.setMany(state, action.payload);
      })
      .addCase(createBillingRecord.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg.ownerKey;
        const clientId = action.payload.clientId;
        billingRecordAdapter.addOne(state, action.payload);
        
        if (!state.byOwnerKey[ownerKey]) state.byOwnerKey[ownerKey] = [];
        if (!state.byOwnerKey[ownerKey].includes(action.payload.id)) {
          state.byOwnerKey[ownerKey].push(action.payload.id);
        }
        
        if (!state.byClient[clientId]) state.byClient[clientId] = [];
        if (!state.byClient[clientId].includes(action.payload.id)) {
          state.byClient[clientId].push(action.payload.id);
        }
      })
      .addCase(createBillingRecordFromSession.fulfilled, (state, action) => {
        const clientId = action.payload.clientId;
        billingRecordAdapter.addOne(state, action.payload);
        
        if (!state.byClient[clientId]) state.byClient[clientId] = [];
        if (!state.byClient[clientId].includes(action.payload.id)) {
          state.byClient[clientId].push(action.payload.id);
        }
      })
      .addCase(updateBillingRecord.fulfilled, (state, action) => {
        billingRecordAdapter.upsertOne(state, action.payload);
      })
      .addCase(deleteBillingRecord.fulfilled, (state, action) => {
        const recordId = action.payload;
        billingRecordAdapter.removeOne(state, recordId);
        
        Object.keys(state.byOwnerKey).forEach(key => {
          state.byOwnerKey[key] = state.byOwnerKey[key].filter(id => id !== recordId);
        });
        Object.keys(state.byClient).forEach(key => {
          state.byClient[key] = state.byClient[key].filter(id => id !== recordId);
        });
      })
      .addCase(createSuperbill.fulfilled, (state, action) => {
        // Update billing records with superbillId and status after superbill creation
        const superbill = action.payload;
        if (superbill.billingRecordIds) {
          superbill.billingRecordIds.forEach(recordId => {
            const record = state.entities[recordId];
            if (record) {
              record.superbillId = superbill.id;
              record.status = 'Billed';
            }
          });
        }
      })
      .addCase(processBillingRecordPayment.fulfilled, (state, action) => {
        billingRecordAdapter.upsertOne(state, action.payload);
      });
  }
});

const { selectById, selectAll } = billingRecordAdapter.getSelectors();

export const selectBillingRecordsByOwnerKey = (ownerKey) => createSelector(
  [state => state.billingRecords.byOwnerKey, state => state.billingRecords],
  (byOwnerKey, billingRecordsState) => {
    const recordIds = byOwnerKey[ownerKey] || [];
    return recordIds.map(id => selectById(billingRecordsState, id)).filter(Boolean);
  }
);

export const selectBillingRecordsByClient = (clientId) => createSelector(
  [state => state.billingRecords.byClient, state => state.billingRecords],
  (byClient, billingRecordsState) => {
    const recordIds = byClient[clientId] || [];
    return recordIds.map(id => selectById(billingRecordsState, id)).filter(Boolean);
  }
);

export const selectBillingRecordById = (id) => state => selectById(state.billingRecords, id);

export const selectBillingRecordsByStatus = (clientId, status) => createSelector(
  [selectBillingRecordsByClient(clientId)],
  (records) => records.filter(r => r.status === status)
);

export const selectAvailableBillingRecords = (clientId) => createSelector(
  [selectBillingRecordsByClient(clientId)],
  (records) => records.filter(r => !r.superbillId)
);

export default billingRecordsSlice.reducer;
